import { useState } from "react";

import Like from "@/src/common/icons/Like";
import { ClickFromType } from "@/src/common/links/FavoriteLink";
import { useIdLikedContext } from "@/src/contexts";
import type { Output } from "@Components/Hooks/useAjax";
import useAjax from "@Components/Hooks/useAjax";
import { AjaxMethod } from "@Components/Modules/Ajax";
import { onDefaultKeyboardUp, urlFormat } from "@Components/Modules/Helpers";
import { Follow } from "@Routes/FollowLinks";
import * as stylex from "@stylexjs/stylex";

import { type PropsWithStyleX, colors } from "../../../styles/tokens.stylex";

export interface ILikeLink extends PropsWithStyleX {
    id: string;
    likesCount: number;
    isSaved: boolean;
    title?: string;
    textStyleX?: stylex.StyleXStyles;
}

export default function LikeLink(props: ILikeLink) {
    const [isSaved, add, remove] = useIdLikedContext(props.id, "discounts");
    const [likeCount, setLikeCount] = useState(
        props.likesCount + (isSaved !== props.isSaved ? (isSaved ? 1 : -1) : 0),
    );

    const { request } = useAjax<Output<boolean>>({
        ajaxAction: urlFormat(Follow, { action: "Discounts" }),
        ajaxMethod: AjaxMethod.Post,
    });

    const onClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        event.stopPropagation();

        if (isSaved) {
            remove();
        } else {
            add();
        }

        setLikeCount(likeCount + (!isSaved ? 1 : -1));
        request(undefined, {
            follow: !isSaved,
            id: props.id,
            likesCount: likeCount,
            origin: ClickFromType.Undefined,
        });
    };

    return (
        <button
            {...stylex.props(styles.likeLinkComponent, props.styleX)}
            title={props.title}
            onClick={onClick}
            onKeyUp={onDefaultKeyboardUp}
            tabIndex={0}
            type="button"
        >
            <Like className={stylex.props(styles.icon, isSaved && styles.active).className} />
            <p {...stylex.props(styles.text, props.textStyleX)}>{likeCount}</p>
        </button>
    );
}

const styles = stylex.create({
    likeLinkComponent: {
        borderColor: "rgba(0, 0, 0, 0.2)",
        borderRadius: "15px",
        borderStyle: "solid",

        borderWidth: "1px",
        alignItems: "center",
        backgroundColor: "#fff",

        boxShadow: "0 0 10px -5px",

        outline: "none",
        color: "black",
        cursor: "pointer",
        paddingBlock: "0",
        display: "flex",
        justifyContent: "center",
        paddingInline: "10px",
        justifyItems: "center",
        height: "27px",
        lineHeight: "27px",
        width: "auto",
    },

    active: {
        fill: colors.b_BrandMagenta,
    },

    text: {
        margin: 0,
        color: "#222",
        display: "inline-block",
        fontSize: "0.75rem",
        fontWeight: 300,
        lineHeight: "normal",
        textTransform: "uppercase",
        verticalAlign: "middle",
        paddingLeft: "5px",
    },

    icon: {
        fill: "#222",
        transition: "200ms fill ease-out",
        height: "15px",
        width: "15px",
    },
});
